import { CervicalConsistency, CervicalPosition, MembraneStatus } from '../types/cervicalExam'
import { ContractionIntensity, ContractionRestingTone } from '../types/contraction'
import { FHRCategory, FHRDeceleration, FHRMethod, FHRVariability } from '../types/fhr'
import { LaborEntryData, LaborEntryType } from '../types/laborEntry'
import {
  MedicationCategory,
  MedicationRoute,
  MedicationType,
  MedicationUnit,
} from '../types/medication'
import { NarrativeCategory } from '../types/narrative'
import { ProcedureCategory, ProcedureName } from '../types/procedure'
import { LaborVitalSigns } from '../types/vitals'

export const fallbackVitalSigns: LaborVitalSigns = {
  temperature: 98.6,
  bloodPressure: {
    systolic: 120,
    diastolic: 80,
  },
  pulse: 70,
  respiratoryRate: 12,
}

export const fallbackCervicalExam: LaborEntryData<LaborEntryType.CERVICAL_EXAM> = {
  dilation: 0,
  effacement: 0,
  station: -3,
  position: CervicalPosition.POSTERIOR,
  consistency: CervicalConsistency.FIRM,
  membranes: MembraneStatus.INTACT,
  presentingPart: 'Vertex',
}

/**
 * Sample cervical exam sequence representing typical labor progression.
 * Covers admission through complete dilation and descent to crowning.
 * Useful for demos, dev fixtures, and UI testing.
 */
export const sampleCervicalExamProgression: LaborEntryData<LaborEntryType.CERVICAL_EXAM>[] = [
  // Admission — early labor, unfavorable cervix
  {
    dilation: 1,
    effacement: 30,
    station: -3,
    position: CervicalPosition.POSTERIOR,
    consistency: CervicalConsistency.FIRM,
    membranes: MembraneStatus.INTACT,
    presentingPart: 'Vertex',
  },
  // Early latent phase
  {
    dilation: 2,
    effacement: 50,
    station: -3,
    position: CervicalPosition.POSTERIOR,
    consistency: CervicalConsistency.MEDIUM,
    membranes: MembraneStatus.INTACT,
    presentingPart: 'Vertex',
  },
  // Late latent phase — cervix softening and moving forward
  {
    dilation: 3,
    effacement: 70,
    station: -2,
    position: CervicalPosition.MID_POSITION,
    consistency: CervicalConsistency.SOFT,
    membranes: MembraneStatus.INTACT,
    presentingPart: 'Vertex',
  },
  // Active labor begins (≥4 cm by ACOG definition)
  {
    dilation: 4,
    effacement: 80,
    station: -1,
    position: CervicalPosition.MID_POSITION,
    consistency: CervicalConsistency.SOFT,
    membranes: MembraneStatus.INTACT,
    presentingPart: 'Vertex',
  },
  // Active labor — SROM occurs
  {
    dilation: 5,
    effacement: 90,
    station: -1,
    position: CervicalPosition.ANTERIOR,
    consistency: CervicalConsistency.SOFT,
    membranes: MembraneStatus.SROM,
    presentingPart: 'Vertex',
  },
  // Active labor progressing well
  {
    dilation: 7,
    effacement: 100,
    station: 0,
    position: CervicalPosition.ANTERIOR,
    consistency: CervicalConsistency.SOFT,
    membranes: MembraneStatus.SROM,
    presentingPart: 'Vertex',
  },
  // Transition
  {
    dilation: 9,
    effacement: 100,
    station: 1,
    position: CervicalPosition.ANTERIOR,
    consistency: CervicalConsistency.SOFT,
    membranes: MembraneStatus.SROM,
    presentingPart: 'Vertex',
  },
  // Complete — second stage, baby descending
  {
    dilation: 10,
    effacement: 100,
    station: 2,
    position: CervicalPosition.ANTERIOR,
    consistency: CervicalConsistency.SOFT,
    membranes: MembraneStatus.SROM,
    presentingPart: 'Vertex',
  },
  // Crowning
  {
    dilation: 10,
    effacement: 100,
    station: 4,
    position: CervicalPosition.ANTERIOR,
    consistency: CervicalConsistency.SOFT,
    membranes: MembraneStatus.SROM,
    presentingPart: 'Vertex',
  },
]

export const fallbackFHR: LaborEntryData<LaborEntryType.FHR> = {
  baselineBpm: 140,
  variability: FHRVariability.MODERATE,
  accelerations: true,
  decelerations: FHRDeceleration.NONE,
  actions: [],
  category: FHRCategory.I,
  method: FHRMethod.DOPPLER,
}

export const fallbackContraction: LaborEntryData<LaborEntryType.CONTRACTION> = {
  frequency: 5,
  duration: 60,
  intensity: ContractionIntensity.MODERATE,
  restingTone: ContractionRestingTone.SOFT,
  collectedVia: 'manual',
}

export const fallbackMedication = (): LaborEntryData<LaborEntryType.MEDICATION> => ({
  medName: '',
  category: MedicationCategory.INDUCTION,
  type: MedicationType.BOLUS,
  dose: 200,
  unit: MedicationUnit.MG,
  route: MedicationRoute.IV,
  site: '',
  startedAt: Date.now(),
})

export const fallbackNarrative = (): LaborEntryData<LaborEntryType.NARRATIVE> => ({
  timestamp: Date.now(),
  noteBody: '',
  category: NarrativeCategory.GENERAL,
  tags: [],
  isAmended: false,
})

export const fallbackProcedure = (): LaborEntryData<LaborEntryType.PROCEDURE> => ({
  procedureName: ProcedureName.AMNIOTOMY,
  category: ProcedureCategory.INTERVENTION,
  startTime: Date.now(),
  providerId: '',
  details: {},
  isSuccessful: false,
  consentObtained: false,
})

export const getFallbackLaborEntryData: { [key in LaborEntryType]: () => LaborEntryData<key> } = {
  [LaborEntryType.VITALS]: () => fallbackVitalSigns,
  [LaborEntryType.CERVICAL_EXAM]: () => fallbackCervicalExam,
  [LaborEntryType.FHR]: () => fallbackFHR,
  [LaborEntryType.CONTRACTION]: () => fallbackContraction,
  [LaborEntryType.MEDICATION]: fallbackMedication,
  [LaborEntryType.NARRATIVE]: fallbackNarrative,
  [LaborEntryType.PROCEDURE]: fallbackProcedure,
}
